import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState } from "react";
import { isReadOnlyUser } from "../../../../shared/utility";
import ReceivingSubTable2 from "./ReceivingSubtable2";
import AuditActionLink from "../../../audit/AuditActionLink";
import DeveloperBarcode from "../../../controls/DeveloperBarcode";
import NumberInputText from "../../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../../hooks/useCellSaveState";
import CopyableIdentifier from "../../../controls/CopyableIdentifier";

export default function ReceivingSubTable1(props) {
  const {
    data,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    onUpdateReceivingItem,
    closed,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();
  const isReadOnly = isReadOnlyUser();
  const canEditRow = (row) =>
    !isReadOnly &&
    !closed &&
    row.id &&
    !row.pallet_id &&
    typeof onUpdateReceivingItem === "function";
  const cartonCellKey = (item, field) => `carton:${item.id}:${field}`;
  const commitReceivingItem = (item, field, value) =>
    saveCell(cartonCellKey(item, field), () =>
      onUpdateReceivingItem({ id: item.id, [field]: value }, data),
    );

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!subRowDetails[rowData.unique_id]) {
      fetchSubRowDetails(rowData);
    }
  };

  const allowExpansion = (rowData) => {
    return rowData.pallet_id;
  };

  function rowExpansionTemplate(data) {
    return (
      <ReceivingSubTable2
        data={data}
        loadingRows={subLoadingRows}
        rowDetails={subRowDetails}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  }

  const bodyProductName = (item) => {
    return (
      <div>
        {item.product_name} <br />{" "}
        <span className="font-bold">PLU: {item.plu}</span>
      </div>
    );
  };

  if (loadingRows[data.unique_id]) {
    return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
  }

  const detail = rowDetails[data.unique_id];
  if (!detail) return null;

  const barcodeBody = (data) => {
    return (
      <div>
        <CopyableIdentifier
          value={data.barcode}
          description="full barcode"
        />
        <DeveloperBarcode value={data.barcode} compact />
      </div>
    );
  };

  const lotNumberBody = (item) => (
    <CopyableIdentifier value={item.lot_number} description="lot number" />
  );

  const bodySubItemWeight = (item) => {
    return (
      <div className="space-y-1">
        <NumberInputText
          value={item.total_weight_in_kg}
          aria-label="Total weight in kilograms"
          className="receiving-table-number"
          inputClassName="receiving-table-input"
          disabled={!canEditRow(item)}
          saveStatus={getSaveStatus(
            cartonCellKey(item, "total_weight_in_kg"),
          )}
          onCommit={(value) =>
            commitReceivingItem(item, "total_weight_in_kg", value)
          }
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0}
          suffix=" kg"
        />
        <NumberInputText
          value={item.total_weight_in_lbs}
          aria-label="Total weight in pounds"
          className="receiving-table-number"
          inputClassName="receiving-table-input"
          disabled={!canEditRow(item)}
          saveStatus={getSaveStatus(
            cartonCellKey(item, "total_weight_in_lbs"),
          )}
          onCommit={(value) =>
            commitReceivingItem(item, "total_weight_in_lbs", value)
          }
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0}
          suffix=" lbs"
        />
      </div>
    );
  };

  const bodyItemPieceCount = (item) => {
    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label="Piece count"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "total_pieces"))}
        onCommit={(value) =>
          commitReceivingItem(item, "total_pieces", value)
        }
        minFractionDigits={0}
        maxFractionDigits={0}
        min={0}
        suffix=" pieces"
      />
    );
  };

  const bodyParentUnitCost = (item) => {
    return (
      <NumberInputText
        value={item.unit_cost}
        aria-label="Unit cost"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "unit_cost"))}
        onCommit={(value) => commitReceivingItem(item, "unit_cost", value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentTotalCost = (item) => {
    return (
      <NumberInputText
        value={item.total_cost}
        aria-label="Total cost"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEditRow(item)}
        saveStatus={getSaveStatus(cartonCellKey(item, "total_cost"))}
        onCommit={(value) => commitReceivingItem(item, "total_cost", value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const cartonCountBody = (item) => {
    return (
      <NumberInputText
        value={item.total_cartons}
        aria-label="Carton count"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={0}
        maxFractionDigits={0}
        suffix=" cartons"
      />
    );
  };

  return (
    <div>
      <DataTable
        value={rowDetails[data.unique_id]}
        expandedRows={expandedRows}
        onRowToggle={(e) => {
          setExpandedRows(e.data);
        }}
        onRowExpand={onRowExpand}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="unique_id"
        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm receiving-sub-items-table-1"
        cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm inv-sub-item"
        selectionMode="checkbox"
        selection={selectedItems}
        onSelectionChange={(e) => setSelectedItems(e.value)}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "3rem" }}
        ></Column>
        <Column
          header="Product Name"
          body={bodyProductName}
          style={{ width: "17%" }}
        />
        <Column
          header="Lot Number"
          body={lotNumberBody}
          style={{ width: "10%" }}
        />
        <Column header="Barcode" body={barcodeBody} style={{ width: "24%" }} />
        <Column
          header="Carton Count"
          body={cartonCountBody}
          style={{ width: "8%", textAlign: "right" }}
        />
        <Column
          header="Total Weight"
          body={bodySubItemWeight}
          style={{ width: "10%", textAlign: "right" }}
        />
        <Column
          header="Piece Count"
          body={bodyItemPieceCount}
          style={{ width: "8%", textAlign: "right" }}
        />
        <Column
          header="Unit Cost"
          body={bodyParentUnitCost}
          style={{ width: "7%", textAlign: "right" }}
        />
        <Column
          header="Total Cost"
          body={bodyParentTotalCost}
          style={{ width: "7%", textAlign: "right" }}
        />
        <Column
          body={bodyParentLocation}
          header="Location"
          style={{ width: "10%" }}
        />
        <Column
          header=""
          body={(rowData) =>
            rowData.id ? (
              <AuditActionLink
                slug="receiving-item"
                id={rowData.id}
                name={rowData.lot_number || rowData.barcode}
              />
            ) : null
          }
          style={{ width: "6%" }}
        />
        <Column expander={allowExpansion} style={{ width: "6%" }} />
      </DataTable>
    </div>
  );
}
